<template>
  <div class="v2-blog-post">
    <app-section v-if="post" theme="peach grid" class="content-section">
      <div class="post-content">
        <span class="bg-sticker star-mint d-none d-md-block">
          <img v-lazy="starMintDarkSticker" :alt="`sticker star mint`" />
        </span>
        <span class="bg-sticker star-orange d-none d-md-block">
          <img v-lazy="starOrangeDarkSticker" :alt="`sticker star orange`" />
        </span>

        <h1 :class="`sticker sticker-${stickerIndex}`">
          {{ title }}
        </h1>

        <div
          v-if="post.use_chapter_description && post.chapter"
          class="chapter-description mb-3"
          v-html="post.chapter[`description_${locale}`]"
        />

        <component :is="v2Description" />

        <div class="author">
          {{ t("by Ersties") }}
        </div>

        <span class="bg-sticker star-pink d-none d-md-block">
          <img v-lazy="starPinkDarkSticker" :alt="`sticker star`" />
        </span>

        <span class="bg-sticker star-white d-none d-md-block">
          <img v-lazy="starWhiteDarkSticker" :alt="`sticker star`" />
        </span>
      </div>
    </app-section>
    <app-section v-else theme="peach grid" class="blog-post-placeholder">
      <spinner />
    </app-section>

    <app-section theme="nude" wave-top>
      <blog-related-posts-v2 v-if="relatedPosts.length" :posts="relatedPosts" />
      <div v-else class="post-related-placeholder"></div>
    </app-section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, PropType } from "vue";
import { useVisitStore } from "@/store/visit";
import { useModalStore } from "@/store/modal";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import AppSection from "@/components/v2/section/index.vue";
import BlogRelatedPostsV2 from "@/components/v2/blog-related-posts/index.vue";
import BlogPlayer from "@/components/blog-player/index.vue";
import { amplitudeTrack } from "@/utils/amplitude";
import { getBlogPostThumb, getBlogPostVideoMedia, getBlogPostVideoPoster } from "@/utils/mediaHelper";
import { BlogPost } from "@/interfaces/Blog/BlogPost.ts";
import { useFeatureStore } from "@/store/feature.ts";
import { getCookie } from "@/utils/cookies.ts";
import { scrollIntoElement } from "@/utils/domHelper.ts";
import browserHelper from "@/utils/browserHelper.ts";
import Spinner from "@/components/spinner/index.vue";
import { ModalType } from "@/interfaces/ModalType.ts";
import starMintDarkSticker from "@/assets/v2/images/stickers/star-mint-dark.svg";
import starOrangeDarkSticker from "@/assets/v2/images/stickers/star-orange-dark.svg";
import starPinkDarkSticker from "@/assets/v2/images/stickers/star-pink-dark.svg";
import starWhiteDarkSticker from "@/assets/v2/images/stickers/star-white-dark.svg";

defineOptions({
  name: "blog-post-v2",
});

const props = defineProps({
  post: {
    type: Object as PropType<BlogPost>,
    default: null,
  },
  relatedPosts: {
    type: Array as PropType<BlogPost[]>,
    default: () => [],
  },
});

const visitStore = useVisitStore();
const modalStore = useModalStore();
const featureStore = useFeatureStore();

const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();

const hasAgeVerificationFeature = computed(
  () => !browserHelper.isCrawlerBot && (getCookie("av_enabled") || featureStore.hasAgeVerificationFeature),
);
const title = computed(() => props.post?.[`title_${locale.value}`]);
let campaign: string | null = null;

const fixImages = (images: HTMLElement[]) => {
  const maxWidth = 1140;

  images.forEach((image: HTMLElement, index) => {
    image.setAttribute("alt", `${title.value} Image ${index + 1}`);

    let path = "";
    const src = image.getAttribute("src");

    if (src) {
      try {
        const url = new URL(src);
        path = url.pathname;
      } catch {
        path = src;
      }
    }

    const lastSlashIndex = path.lastIndexOf("/");
    const dir = path.substring(0, lastSlashIndex);
    const name = path.substring(lastSlashIndex + 1);

    const width = parseInt(image.style.width) || parseInt(image.getAttribute("width") as string) || 900;

    image.setAttribute("src", getBlogPostThumb(dir, name, width * 2));

    if (width < maxWidth) {
      return;
    }

    image.removeAttribute("height");
    image.removeAttribute("width");
  });
};

const fixLinks = (links: HTMLElement[]) => {
  let campaignSet = false;

  links.forEach((link: HTMLElement) => {
    const content = link.textContent || "";

    if (["free ersties sign up", "melde dich kostenlos bei ersties an"].includes(content.toLowerCase())) {
      link.classList.add("btn");
      link.classList.add("btn-primary");
    }

    const regex = /<<<<.*>>>/;
    const match = regex.exec(content);

    if (match) {
      link.textContent = content.replace(/[<>]/g, "");
    }

    if (!campaignSet) {
      const href = link.getAttribute("href");
      const campaignParam = href?.split("?")[1];
      campaign = campaignParam?.split("=")[1] || null;
      campaignSet = true;
    }

    link.removeAttribute("href");
    link.setAttribute("data-is-blog-link", "true");
  });
};

const v2Description = computed(() => {
  if (!props.post) {
    return { template: "<div></div>" };
  }

  const description = document.createElement("div");
  const sfwPrefix = hasAgeVerificationFeature.value && !props.post.sfw ? "sfw_" : "";
  description.innerHTML = props.post[`${sfwPrefix}description_${locale.value}`] as string;
  const images = [...description.getElementsByTagName("img")];
  fixImages(images as HTMLElement[]);
  const links = [...description.querySelectorAll("[href*='/welcome'], [href*='/tourpage']")];
  fixLinks(links as HTMLElement[]);

  const videoPlaceholder = description.querySelector("#embed_video") as HTMLElement | null;

  if (videoPlaceholder) {
    const player = document.createElement("blog-player");
    player.setAttribute("id", "post-video");
    player.setAttribute("source", getBlogPostVideoMedia(props.post[`${sfwPrefix}video`] as string));
    player.setAttribute("poster", getBlogPostVideoPoster(props.post[`${sfwPrefix}video_thumb`] as string));
    player.setAttribute("preview-duration", props.post[`${sfwPrefix}preview_duration`]?.toString() || "");
    player.setAttribute("campaign", campaign as string);
    videoPlaceholder.innerHTML = "";
    videoPlaceholder.appendChild(player);
  }

  return {
    template: `<div class='content' id='blog_post_content'>${description.innerHTML}</div>`,
    components: { BlogPlayer },
    setup() {
      const handleClick = async (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const link = target.closest('[data-is-blog-link="true"]');

        if (!link) {
          return;
        }

        await router.replace({
          ...route,
          ...{ query: { w: campaign }, hash: "#" },
        });
        visitStore.trackVisit();

        const modalProps: ModalType = {
          id: "modal-signup",
        };

        if (props.post?.chapter) {
          modalProps.redirectUrl = `/shoot/${props.post?.chapter.gallery_id}`;
        }

        modalStore.showModal(modalProps);

        if (target.tagName === "IMG" || target.querySelector("img") !== null) {
          amplitudeTrack("Blog post: image click");
        } else if (["free ersties sign up", "melde dich kostenlos bei ersties an"].includes(target.textContent?.toLowerCase() || "")) {
          amplitudeTrack("Blog post: CTA button click");
        } else {
          amplitudeTrack("Blog post: link click");
        }
      };

      onMounted(() => {
        const contentEl = document.getElementById("blog_post_content");

        if (contentEl) {
          contentEl.addEventListener("click", handleClick);
        }

        nextTick(() => {
          if (!videoPlaceholder || `#${videoPlaceholder.id}` !== route?.hash) {
            return;
          }

          const documentVideoPlaceholder = document.getElementById(videoPlaceholder.id) as HTMLElement;
          scrollIntoElement(documentVideoPlaceholder);
        });
      });
    },
  };
});

const stickerIndex = computed(() => {
  if (!title.value) {
    return 0;
  }

  return title.value.length % 10;
});
</script>

<style lang="scss">
@import "./index";
</style>
